import { useEffect, useState } from 'react';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const PLAN_KEYS = ['classic', 'premium', 'ultra'];

function Field({ label, help, value, onChange, type = 'text' }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {help && <p className="text-xs text-slate-500">{help}</p>}
      <input
        type={type}
        className="w-full border rounded p-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export default function AdminFinancialConfig() {
  const [config, setConfig] = useState(null);
  const [help, setHelp] = useState({});
  const [draft, setDraft] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [adminId, setAdminId] = useState('admin');

  useEffect(() => {
    api.get('/admin/financial-config').then(({ data }) => {
      setConfig(data.config);
      setDraft(JSON.parse(JSON.stringify(data.config)));
      setHelp(data.field_help || {});
    }).catch(() => {});
  }, []);

  const setIntro = (field, val) => {
    setDraft((d) => ({
      ...d,
      introductory: { ...d.introductory, [field]: val },
    }));
  };

  const setPlan = (key, field, val) => {
    setDraft((d) => ({
      ...d,
      plans: {
        ...d.plans,
        [key]: { ...d.plans[key], [field]: val },
      },
    }));
  };

  const setSection = (section, field, val) => {
    setDraft((d) => ({
      ...d,
      [section]: { ...d[section], [field]: val },
    }));
  };

  const buildUpdates = () => {
    if (!draft || !config) return {};
    return {
      introductory: draft.introductory,
      plans: draft.plans,
      wallet: draft.wallet,
      booking: draft.booking,
      cancellation: {
        customer_travel_provider_share_percent: draft.cancellation?.customer_travel_provider_share_percent,
        customer_travel_platform_share_percent: draft.cancellation?.customer_travel_platform_share_percent,
        provider_penalty_after_payment: draft.cancellation?.provider_penalty_after_payment,
      },
      payout: draft.payout,
      business_timezone: draft.business_timezone,
    };
  };

  const save = async () => {
    try {
      const { data } = await api.post('/admin/financial-config', {
        updates: buildUpdates(),
        admin_id: adminId,
        password,
      });
      setConfig(data.config);
      setDraft(JSON.parse(JSON.stringify(data.config)));
      setConfirmOpen(false);
      setPassword('');
      toast.success('Financial configuration updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  if (!draft) return <div className="p-5">Loading...</div>;

  return (
    <div className="resqly-shell">
      <div className="resqly-frame">
        <MobileShell title="Financial Configuration" header>
          <div className="px-5 py-5 space-y-6">
            <section className="resqly-card p-4 space-y-3">
              <h2 className="font-semibold">Provider Introduction</h2>
              <Field
                label="Introductory completed orders"
                help={help['introductory.completed_order_count']}
                value={draft.introductory?.completed_order_count ?? ''}
                onChange={(v) => setIntro('completed_order_count', Number(v))}
                type="number"
              />
              <Field
                label="Introductory commission %"
                help={help['introductory.commission_percent']}
                value={draft.introductory?.commission_percent ?? ''}
                onChange={(v) => setIntro('commission_percent', Number(v))}
                type="number"
              />
            </section>

            <section className="resqly-card p-4 space-y-4">
              <h2 className="font-semibold">Plans</h2>
              {PLAN_KEYS.map((key) => (
                <div key={key} className="border-t pt-3 space-y-2" data-testid={`admin-plan-${key}`}>
                  <h3 className="text-sm font-medium capitalize">{key}</h3>
                  <Field label="Name" help={help[`plans.${key}.name`]} value={draft.plans?.[key]?.name ?? ''} onChange={(v) => setPlan(key, 'name', v)} />
                  <Field label="Recharge price" help={help[`plans.${key}.price`]} value={draft.plans?.[key]?.price ?? ''} onChange={(v) => setPlan(key, 'price', Number(v))} type="number" />
                  <Field label="Commission %" help={help[`plans.${key}.commission_percent`]} value={draft.plans?.[key]?.commission_percent ?? ''} onChange={(v) => setPlan(key, 'commission_percent', Number(v))} type="number" />
                </div>
              ))}
            </section>

            <section className="resqly-card p-4 space-y-3">
              <h2 className="font-semibold">Wallet</h2>
              <Field
                label="Low balance threshold %"
                help={help['wallet.low_balance_threshold_percent']}
                value={draft.wallet?.low_balance_threshold_percent ?? ''}
                onChange={(v) => setSection('wallet', 'low_balance_threshold_percent', Number(v))}
                type="number"
              />
            </section>

            <section className="resqly-card p-4 space-y-3">
              <h2 className="font-semibold">Booking Payment</h2>
              <Field
                label="Customer payment timeout (minutes)"
                help={help['booking.customer_payment_timeout_minutes']}
                value={draft.booking?.customer_payment_timeout_minutes ?? ''}
                onChange={(v) => setSection('booking', 'customer_payment_timeout_minutes', Number(v))}
                type="number"
              />
            </section>

            <section className="resqly-card p-4 space-y-3">
              <h2 className="font-semibold">Cancellation</h2>
              <Field
                label="Customer travel provider share %"
                help={help['cancellation.customer_travel_provider_share_percent']}
                value={draft.cancellation?.customer_travel_provider_share_percent ?? ''}
                onChange={(v) => setSection('cancellation', 'customer_travel_provider_share_percent', Number(v))}
                type="number"
              />
              <Field
                label="Customer travel RESQLY share %"
                help={help['cancellation.customer_travel_platform_share_percent']}
                value={draft.cancellation?.customer_travel_platform_share_percent ?? ''}
                onChange={(v) => setSection('cancellation', 'customer_travel_platform_share_percent', Number(v))}
                type="number"
              />
              <Field
                label="Provider cancellation penalty"
                help={help['cancellation.provider_penalty_after_payment']}
                value={draft.cancellation?.provider_penalty_after_payment ?? ''}
                onChange={(v) => setSection('cancellation', 'provider_penalty_after_payment', Number(v))}
                type="number"
              />
            </section>

            <section className="resqly-card p-4 space-y-3">
              <h2 className="font-semibold">Payout</h2>
              <Field
                label="Payout SLA hours"
                help={help['payout.sla_hours']}
                value={draft.payout?.sla_hours ?? ''}
                onChange={(v) => setSection('payout', 'sla_hours', Number(v))}
                type="number"
              />
            </section>

            <Button onClick={() => setConfirmOpen(true)} data-testid="open-financial-confirm">Save Changes</Button>

            {confirmOpen && (
              <div className="resqly-card p-4 space-y-3 border-2 border-blue-200" data-testid="financial-confirm-dialog">
                <h3 className="font-semibold">Confirm Financial Change</h3>
                <p className="text-xs text-slate-500">Review and confirm with admin credentials.</p>
                <input className="w-full border rounded p-2" placeholder="Admin ID" value={adminId} onChange={(e) => setAdminId(e.target.value)} />
                <input type="password" className="w-full border rounded p-2" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setConfirmOpen(false)}>Cancel</Button>
                  <Button className="flex-1" data-testid="confirm-financial-change" onClick={save}>Confirm</Button>
                </div>
              </div>
            )}
          </div>
        </MobileShell>
      </div>
    </div>
  );
}
